import { ComponentType } from "react";

import { css } from "@compiled/react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { useHeroState } from "../util/hero-context";

type HeroCardProps = {
    readonly eyebrow: string;
    readonly title: string;
    readonly image: string;
    readonly BackgroundImage: ComponentType;
    readonly idx: number;
    readonly label: string;
};

const CardStyles = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    textAlign: "left",
    padding: "0",
    width: "260px",
    height: "496px",
    minWidth: "260px",
    minHeight: "496px",
    border: "0",
    borderRadius: "22px",
    background: "var(--Primary-White, #FFF)",
    boxShadow:
        "0px 1px 1px 0px rgba(9, 30, 66, 0.25), 0px 0px 1px 0px rgba(9, 30, 66, 0.31)",
    cursor: "pointer",
    transition: "300ms",
    overflow: "clip",
    [media.above.lg]: {
        ["&:hover"]: {
            boxShadow: token("elevation.shadow.overlay"),
            transform: "translateY(-10px)",
            ["svg"]: {
                opacity: "1"
            }
        }
    },
    [media.only.md]: {
        width: "261px",
        minWidth: "216px",
        height: "400px",
        minHeight: "400px"
    },
    [media.only.sm]: {
        width: "261px",
        minWidth: "216px",
        height: "400px",
        minHeight: "400px"
    },
    [media.below.sm]: {
        height: "320px",
        minHeight: "320px",
        width: "261px",
        minWidth: "216px"
    }
});

const textStyles = css({
    paddingInlineStart: "34px"
});

const eyebrowStyles = css({
    color: "var(--Primary-Black, var(--DarkNeutral-Opaque-DarkNeutral-100, #101214))",
    fontFamily: "var(--atlassian-recommended-system-fonts)",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "16px",
    letterSpacing: "0.24px",
    textTransform: "uppercase",
    paddingBlockStart: "29px",
    [media.below.sm]: {
        fontSize: "9px",
        lineHeight: "13px"
    }
});

const titleStyles = css({
    width: "158px",
    color: "var(--Primary-Black, var(--DarkNeutral-Opaque-DarkNeutral-100, #101214))",
    fontFeatureSettings: "'clig' off, 'liga' off",
    fontFamily: "var(--atlassian-recommended-system-fonts)",
    fontSize: "24px",
    fontStyle: "normal",
    fontWeight: "700",
    lineHeight: "30px",
    paddingBlockStart: "7px",
    wordWrap: "break-word",
    hyphens: "auto",
    [media.only.md]: {
        width: "142px",
        marginBlockEnd: "24px",
        fontSize: "20px"
    },
    [media.only.sm]: {
        width: "142px",
        marginBlockEnd: "24px",
        fontSize: "20px"
    },
    [media.below.sm]: {
        width: "142px",
        marginBlockEnd: "24px",
        height: "58px",
        color: "#000",
        fontSize: "20px",
        fontWeight: "600",
        lineHeight: "28px"
    }
});

const imageContainer = css({
    position: "relative",
    overflowY: "clip"
});

const imageStyles = css({
    width: "227px",
    height: "339px",
    borderRadius: "22px",
    marginInlineStart: "34px",
    position: "absolute",
    insetBlockEnd: "0",
    [media.below.lg]: {
        insetBlockStart: "0"
    }
});

export const HeroCard = ({
    eyebrow,
    title,
    image,
    BackgroundImage,
    idx,
    label
}: HeroCardProps) => {
    const { openModal } = useHeroState();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const handleClick = () => {
        openModal(idx);
        createAnalyticsEvent({
            eventType: "ui",
            action: "clicked",
            attributes: {
                name: `atlas-55799-${label}-modal-opened`,
                section: "hero-section"
            }
        }).fire();
    };

    return (
        <button css={CardStyles} onClick={handleClick}>
            <div css={textStyles}>
                <div css={eyebrowStyles}>{eyebrow}</div>
                <div css={titleStyles}>{title}</div>
            </div>
            <div css={imageContainer}>
                <BackgroundImage />
                <div css={imageStyles}>
                    <img src={image} width={227} alt="" />
                </div>
            </div>
        </button>
    );
};
